import type { RolePermissionsResponse } from "@credit-acceptance/step6-bff-client";

import type { SectionCapability, WorkbenchRoleCapabilities, WorkbenchSectionId } from "./types";

/**
 * Base `programRoleSet` marker each Step6 section is gated by (matrix §2a).
 * A role's flat `permissions[]` array is expected to carry these bare
 * strings (section visible) plus `${marker}_W` (write) / `${marker}_R` (forced
 * read-only) variants where applicable.
 */
const TAB_MARKERS: Record<WorkbenchSectionId, string> = {
  dealStatus: "STATUS",
  financial: "FINANCIAL",
  vehicle: "VEHICLE",
  driver: "DRIVER",
  secondSigner: "SECONDSIGNER",
  internal: "INTERNAL",
};

/**
 * Fail-safe verdict used for a section when the source response can't be
 * trusted (unknown role, empty/missing `permissions`, malformed payload).
 * Matches the doc's "fail-safe default: read-only if unknown" principle —
 * here that means hidden, since visibility can't be assumed either.
 */
const DENY_ALL_CAPABILITY: SectionCapability = {
  visible: false,
  writable: false,
  forcedReadOnly: true,
};

/**
 * Derives one section's visible/writable/forcedReadOnly verdict from a
 * permission set and its base marker. `_R` is scoped to only the section it
 * names — the legacy `isStep6ApplicantInfoDisable` cross-coupling (any one
 * of VEHICLE_R/FINANCIAL_R/DRIVER_R/SECONDSIGNER_R forcing every section
 * read-only) is deliberately NOT reproduced here; see matrix §2a/§6.4.
 * @param {ReadonlySet<string>} permissionSet - Upper-cased permission strings for the role.
 * @param {string} marker - The section's base marker, e.g. `"VEHICLE"`.
 * @returns {SectionCapability} The section's parsed capability.
 */
function deriveSectionCapability(permissionSet: ReadonlySet<string>, marker: string): SectionCapability {
  const forcedReadOnly = permissionSet.has(`${marker}_R`);
  return {
    visible: permissionSet.has(marker),
    writable: permissionSet.has(`${marker}_W`) && !forcedReadOnly,
    forcedReadOnly,
  };
}

/**
 * Converts the raw `RolePermissionsResponse` from
 * `ProgramRoleCapabilitiesApi.getRolePermissions` into the Workbench's
 * per-section capability shape. Permission strings unrelated to Step6 (e.g.
 * `DEALERPROFILE_*`, `VSCSETUP*`, `USERSETUP_W`) belong to other CAPS/DMS
 * modules and are ignored here.
 * @param {RolePermissionsResponse | undefined} response - The raw BFF response, or undefined on fetch failure.
 * @returns {WorkbenchRoleCapabilities} The parsed, Workbench-usable capabilities. Fails safe (every section hidden/read-only) when `response` is undefined or carries no permissions.
 */
export function parseRolePermissions(response: RolePermissionsResponse | undefined): WorkbenchRoleCapabilities {
  const roleName = response?.roleName ?? "";
  const userType = response?.userType ?? "";
  const permissionSet = new Set((response?.permissions ?? []).map((permission) => permission.toUpperCase()));

  const sections = Object.fromEntries(
    (Object.entries(TAB_MARKERS) as [WorkbenchSectionId, string][]).map(([sectionId, marker]) => [
      sectionId,
      permissionSet.size === 0 ? DENY_ALL_CAPABILITY : deriveSectionCapability(permissionSet, marker),
    ])
  ) as Record<WorkbenchSectionId, SectionCapability>;

  return {
    roleName,
    userType,
    isCacUser: userType.toUpperCase() === "CAC",
    sections,
    canExit: permissionSet.has("EXIT"),
  };
}
